// frontend/src/internal/InternalSelectOrg.js
// ログイン後の組織選択ページ
// 全 organization を一覧表示し、選択すると selectedOrg に保存 → /admin などへナビゲート可能

import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useInternalStaff } from '../contexts/InternalStaffContext';

const InternalSelectOrg = () => {
  const { internalStaff, selectedOrg, selectOrganization, logout, loading } = useInternalStaff();
  const [organizations, setOrganizations] = useState([]);
  const [filter, setFilter] = useState('');
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (!internalStaff) return;
    (async () => {
      setFetching(true);
      try {
        // organizations テーブルを取得 (RLS で自社スタッフは全件見える想定)
        const { data, error } = await supabase
          .from('organizations')
          .select('id, name, created_at')
          .order('name');
        if (error) {
          // テーブル名が違うかもしれないので fallback で organization_settings 等を試す
          const { data: alt } = await supabase
            .from('organization_settings')
            .select('organization_id, organization_name')
            .order('organization_name');
          setOrganizations((alt || []).map((o) => ({
            id: o.organization_id, name: o.organization_name || o.organization_id,
          })));
        } else {
          setOrganizations(data || []);
        }
      } catch (e) {
        console.error('組織一覧取得エラー:', e);
      } finally {
        setFetching(false);
      }
    })();
  }, [internalStaff]);

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center' }}>読み込み中...</div>;
  }
  if (!internalStaff) {
    return <Navigate to="/internal/login" replace />;
  }

  const filtered = organizations.filter((o) =>
    !filter || (o.name || o.id || '').toLowerCase().includes(filter.toLowerCase())
  );

  const handleSelect = async (org) => {
    await selectOrganization(org);
    // 🔥 OrganizationContext を新しい組織情報で再初期化させるためハードリロード。
    //   /home に直接ジャンプ → その病院の admin として「普通にログインしたのと全く同じ体験」。
    //   編集モードの ON/OFF や監査ログ確認は、上部バナー「ダッシュボードへ」から開く。
    window.location.href = '/home';
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc, #eff6ff)',
      padding: '24px',
    }}>
      <div style={{ maxWidth: '720px', margin: '0 auto' }}>
        {/* ヘッダー */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: '24px',
        }}>
          <div>
            <div style={{
              display: 'inline-block', padding: '4px 10px',
              background: '#fef3c7', color: '#92400e',
              borderRadius: '12px', fontSize: '11px', fontWeight: 700,
              marginBottom: '6px',
            }}>
              🔧 Internal
            </div>
            <h1 style={{ margin: 0, fontSize: '24px', color: '#0f172a' }}>組織を選択</h1>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
              {internalStaff.name} ({internalStaff.role}) としてログイン中
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {internalStaff.role === 'admin' && (
              <a href="/internal/staff-management" style={{
                padding: '8px 14px', background: '#fef3c7', color: '#92400e',
                borderRadius: '8px', fontSize: '13px',
                textDecoration: 'none', fontWeight: 600,
              }}>
                🔧 開発者管理
              </a>
            )}
            <button onClick={logout} style={{
              padding: '8px 14px', background: '#e5e7eb', border: 'none',
              borderRadius: '8px', fontSize: '13px', cursor: 'pointer',
            }}>
              ログアウト
            </button>
          </div>
        </div>

        {/* 検索 */}
        <input
          type="text" value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="🔍 組織名で検索"
          style={{
            width: '100%', padding: '10px 14px',
            borderRadius: '10px', border: '1px solid #d1d5db',
            fontSize: '14px', marginBottom: '16px',
          }}
        />

        {/* 組織一覧 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {fetching && <div style={{ color: '#64748b', textAlign: 'center', padding: 20 }}>取得中...</div>}
          {!fetching && filtered.length === 0 && (
            <div style={{ color: '#64748b', textAlign: 'center', padding: 20 }}>
              組織が見つかりません
            </div>
          )}
          {filtered.map((org) => (
            <div
              key={org.id}
              onClick={() => handleSelect(org)}
              style={{
                background: 'white', padding: '14px 18px',
                borderRadius: '12px',
                border: selectedOrg?.id === org.id ? '2px solid #2563eb' : '1px solid #e5e7eb',
                cursor: 'pointer',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = '#93c5fd'}
              onMouseLeave={(e) => {
                if (selectedOrg?.id !== org.id) e.currentTarget.style.borderColor = '#e5e7eb';
              }}
            >
              <div>
                <div style={{ fontWeight: 700, color: '#0f172a' }}>{org.name || '(名称未設定)'}</div>
                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: 2 }}>{org.id}</div>
              </div>
              <div style={{ color: '#2563eb', fontSize: '13px', fontWeight: 600 }}>選択 →</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InternalSelectOrg;
